import {collection, query, where} from 'firebase/firestore';
import {useMemo} from 'react';
import {useFirestore, useFirestoreCollectionData} from 'reactfire';
import {useAuthUser} from '../../auth/hooks/useAuthUser';
import {EntityConverter} from '../EntityConverter';
import {FirestoreCollection} from '../FirestoreCollection';
import {Collection} from '../types';
import {DeletedProperty, Entity, EntityWithMetadata, IdField, IdGenerator, MetadataField} from '#common/models/Entity';

export function useCollection<T extends Entity>(
  idGenerator: IdGenerator<T>,
  path: string,
  ...segments: string[]
): Collection<T> {
  const {data: currentUser} = useAuthUser();
  const firestore = useFirestore();

  // Use a stable JSON representation of segments for useMemo dependency
  const segmentsKey = JSON.stringify(segments);

  const reference = useMemo(() => {
    const actualSegments = JSON.parse(segmentsKey) as string[];
    return collection(firestore, path, ...actualSegments).withConverter(new EntityConverter<EntityWithMetadata<T>>());
  }, [firestore, path, segmentsKey]);

  const rawObservable = useFirestoreCollectionData<EntityWithMetadata<T>>(
    query(reference, where([MetadataField, DeletedProperty].join('.'), '==', false)),
    {
      idField: IdField,
      suspense: false,
    },
  );

  const observable = useMemo(
    () => ({
      data: rawObservable.data,
      error: rawObservable.error,
      firstValuePromise: rawObservable.firstValuePromise,
      hasEmitted: rawObservable.hasEmitted,
      isComplete: rawObservable.isComplete,
      status: rawObservable.status,
    }),
    [
      rawObservable.data,
      rawObservable.error,
      rawObservable.firstValuePromise,
      rawObservable.hasEmitted,
      rawObservable.isComplete,
      rawObservable.status,
    ],
  );

  return useMemo(() => {
    const currentUserId = currentUser?.uid ?? '';
    return new FirestoreCollection<T>(idGenerator, currentUserId, reference, observable);
  }, [currentUser?.uid, idGenerator, observable, reference]);
}
